import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultUnitSystem, matchLanguage, type LanguageCode, type UnitSystem } from '@dishly/shared';

export type Theme = 'light' | 'dark';
export type ViewMode = 'grid' | 'list';

interface PrefsState {
  /** Null follows the system setting. */
  theme: Theme | null;
  units: UnitSystem;
  language: LanguageCode;
  view: ViewMode;
}

/** Per-device preferences. Units and language are also saved to the account (see lib/account.ts). */
export const usePrefs = create<PrefsState>()(
  persist(
    (): PrefsState => ({
      theme: null,
      units: defaultUnitSystem(navigator.languages),
      language: matchLanguage(navigator.languages),
      view: 'grid',
    }),
    { name: 'dishly-prefs', version: 1 },
  ),
);

const systemDark = window.matchMedia('(prefers-color-scheme: dark)');

export function resolvedTheme(theme: Theme | null = usePrefs.getState().theme): Theme {
  return theme ?? (systemDark.matches ? 'dark' : 'light');
}

function applyTheme() {
  const theme = resolvedTheme();
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#161311' : '#fbf7f2');
}

applyTheme();
usePrefs.subscribe(applyTheme);
systemDark.addEventListener('change', applyTheme);
